// src/components/Modal.js
import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { FaTimes, FaExclamationTriangle, FaCheckCircle, FaInfoCircle, FaQuestion } from 'react-icons/fa';

/**
 * Enhanced Modal Component
 * 
 * A professional modal dialog component with animations and various states
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isVisible - Whether the modal is visible
 * @param {string} props.title - Modal title
 * @param {string} props.message - Modal message content
 * @param {Function} props.onClose - Function to call when modal is closed
 * @param {Function} props.onConfirm - Optional confirmation function
 * @param {string} props.type - Optional modal type ('error', 'success', 'info', 'warning', 'confirm')
 * @returns {JSX.Element} - Rendered modal component
 */
const Modal = ({ isVisible, title, message, onClose, onConfirm, type = 'info' }) => {
  // Determine icon and colors based on modal type
  const getModalConfig = () => {
    switch (type) {
      case 'error':
        return {
          icon: FaExclamationTriangle,
          iconColor: 'text-red-500',
          iconBg: 'bg-red-100',
          confirmButton: 'bg-red-600 hover:bg-red-700',
          confirmText: 'OK'
        };
      case 'success':
        return {
          icon: FaCheckCircle,
          iconColor: 'text-green-500',
          iconBg: 'bg-green-100',
          confirmButton: 'bg-green-600 hover:bg-green-700',
          confirmText: 'OK'
        };
      case 'warning':
        return {
          icon: FaExclamationTriangle,
          iconColor: 'text-yellow-500',
          iconBg: 'bg-yellow-100',
          confirmButton: 'bg-yellow-600 hover:bg-yellow-700',
          confirmText: 'OK'
        };
      case 'confirm':
        return {
          icon: FaQuestion,
          iconColor: 'text-blue-500',
          iconBg: 'bg-blue-100',
          confirmButton: 'bg-blue-600 hover:bg-blue-700',
          confirmText: 'Confirm'
        };
      case 'info':
      default:
        return {
          icon: FaInfoCircle,
          iconColor: 'text-blue-500',
          iconBg: 'bg-blue-100',
          confirmButton: 'bg-blue-600 hover:bg-blue-700',
          confirmText: 'OK'
        };
    }
  };

  const config = getModalConfig();
  const Icon = config.icon;

  return (
    <Transition appear show={isVisible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <FaTimes className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
                
                {/* Header with Icon */}
                <div className="flex items-center space-x-4">
                  <div className={`${config.iconBg} rounded-full p-3`}>
                    <Icon className={`${config.iconColor} h-6 w-6`} aria-hidden="true" />
                  </div>
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {title}
                  </Dialog.Title>
                </div>
                
                {/* Message */}
                <div className="mt-4 pl-14">
                  <p className="text-sm text-gray-600 whitespace-pre-line">
                    {message}
                  </p>
                </div>

                {/* Buttons */}
                <div className="mt-6 flex justify-end gap-3">
                  {onConfirm && (
                    <button
                      type="button"
                      className={`inline-flex justify-center rounded-md border border-transparent ${config.confirmButton} px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200`}
                      onClick={() => {
                        onConfirm();
                        if (!onClose) onClose();
                      }}
                    >
                      {config.confirmText}
                    </button>
                  )}
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                    onClick={onClose}
                  >
                    {onConfirm ? 'Cancel' : 'Close'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;